// Data contract shared by Studio, the client and the authoritative server.
export const MAP_SCHEMA='turbo-map-colliders@1';
export const OBJECT_SCHEMA='turbo-map-object@1';
export const EDITABLE_MAPS=Object.freeze({living:'Sala',bedroom:'Dormitorio',garden:'Jardín'});
const clone=(value)=>JSON.parse(JSON.stringify(value));
function finite(value,label){
  if(typeof value!=='number'||!Number.isFinite(value)||Math.abs(value)>10000)throw new Error(`${label}: número inválido o fuera de rango.`);
  return value;
}
export function validateColliders(colliders){
  if(!Array.isArray(colliders)||colliders.length>2000)throw new Error('Se admiten hasta 2000 colliders.');
  const ids=new Set();
  return colliders.map((source)=>{
    const c=clone(source);
    if(!/^[a-zA-Z0-9_-]{1,100}$/.test(c.id)||ids.has(c.id))throw new Error('Cada collider necesita un ID único.');
    ids.add(c.id);
    if(!['box','circle'].includes(c.shape))throw new Error('Forma no soportada: usá caja o cilindro vertical.');
    if(typeof c.tag!=='string'||!c.tag.trim()||c.tag.length>100)throw new Error('El objeto necesita un nombre de hasta 100 caracteres.');
    for(const key of ['minX','maxX','minY','maxY','minZ','maxZ'])finite(c[key],key);
    for(const axis of ['X','Y','Z'])if(c[`max${axis}`]-c[`min${axis}`]<.02-1e-9)throw new Error('Cada dimensión debe medir al menos 0,02.');
    if(c.shape==='circle'){
      for(const key of ['x','z','radius'])finite(c[key],key);
      if(c.radius<.01)throw new Error('El radio debe ser positivo.');
      if(Math.abs(c.minX-(c.x-c.radius))>1e-6||Math.abs(c.maxX-(c.x+c.radius))>1e-6||Math.abs(c.minZ-(c.z-c.radius))>1e-6||Math.abs(c.maxZ-(c.z+c.radius))>1e-6)throw new Error('Los límites del cilindro no coinciden con su radio.');
    }
    // Preserve gameplay flags from procedural maps (including collision exclusions).
    return c;
  });
}
export function worldRevision(world){
  const value=JSON.stringify({obstacles:world.obstacles,ramps:world.ramps,width:world.width,depth:world.depth});
  let hash=2166136261;
  for(let i=0;i<value.length;i++)hash=Math.imul(hash^value.charCodeAt(i),16777619);
  return `procedural-1-${(hash>>>0).toString(16)}`;
}
export function documentFromWorld(world){
  if(!Object.hasOwn(EDITABLE_MAPS,world.id))throw new Error('Este escenario no admite edición estática.');
  return {schema:MAP_SCHEMA,mapId:world.id,baseRevision:worldRevision(world),colliders:world.obstacles.map((c,i)=>({...clone(c),id:`${c.tag||'obstacle'}-${i}`}))};
}
export function validateMapDocument(value){
  if(!value||![MAP_SCHEMA,OBJECT_SCHEMA].includes(value.schema))throw new Error('Formato de mapa u objeto no reconocido.');
  if(value.schema===MAP_SCHEMA&&(!Object.hasOwn(EDITABLE_MAPS,value.mapId)||typeof value.baseRevision!=='string'))throw new Error('Escenario o revisión base inválidos.');
  return {...clone(value),colliders:validateColliders(value.colliders)};
}
export function applyMapDocument(world,value){
  const doc=validateMapDocument(value);
  if(doc.schema!==MAP_SCHEMA||doc.mapId!==world.id)throw new Error('El documento pertenece a otro escenario.');
  if(doc.baseRevision!==worldRevision(world))throw new Error('La base del mapa cambió. Revisá las colisiones en Map Studio antes de integrarlas.');
  world.obstacles=doc.colliders.map(({id,...c})=>c);
  return world;
}
export function colliderBounds(c){
  return {x:(c.minX+c.maxX)/2,y:(c.minY+c.maxY)/2,z:(c.minZ+c.maxZ)/2,width:c.maxX-c.minX,height:c.maxY-c.minY,depth:c.maxZ-c.minZ};
}
export function resizeCollider(c,{x,y,z,width,height,depth}){
  const next={...c,minX:x-width/2,maxX:x+width/2,minY:y-height/2,maxY:y+height/2,minZ:z-depth/2,maxZ:z+depth/2};
  if(c.shape==='circle')Object.assign(next,{x,z,radius:width/2,minZ:z-width/2,maxZ:z+width/2});
  return validateColliders([next])[0];
}
export function translateCollider(c,x,y,z){
  const b=colliderBounds(c);return resizeCollider(c,{...b,x:b.x+x,y:b.y+y,z:b.z+z});
}
export function objectFromColliders(colliders){
  if(!colliders.length)throw new Error('Seleccioná un objeto con colliders.');
  const x=(Math.min(...colliders.map(c=>c.minX))+Math.max(...colliders.map(c=>c.maxX)))/2;
  const z=(Math.min(...colliders.map(c=>c.minZ))+Math.max(...colliders.map(c=>c.maxZ)))/2;
  const y=Math.min(...colliders.map(c=>c.minY));
  return {schema:OBJECT_SCHEMA,colliders:colliders.map(c=>translateCollider(c,-x,-y,-z))};
}
export function warningsForDocument(doc){
  const result=[];
  for(let i=0;i<doc.colliders.length;i++){
    const a=doc.colliders[i];
    for(let j=0;j<i;j++){
      const b=doc.colliders[j];
      if(['minX','maxX','minY','maxY','minZ','maxZ'].every(k=>Math.abs(a[k]-b[k])<.001))result.push(`Volúmenes coincidentes: ${a.id} / ${b.id}`);
    }
  }
  return result.slice(0,20);
}
// Opt-in repair in Studio: does not silently change published gameplay.
export function splitLivingSeats(doc){
  if(doc.mapId!=='living')throw new Error('Esta corrección corresponde a la Sala.');
  const result=clone(doc);
  const originals=result.colliders.filter(c=>(c.tag==='sofa'||c.tag==='reading-chair')&&!/-(base|seat|back|arm--?1)$/.test(c.id));
  if(!originals.length)throw new Error('Los asientos ya están divididos o fueron modificados.');
  result.colliders=result.colliders.filter(c=>!originals.includes(c));
  const box=(id,tag,x,z,w,d,lo,hi)=>({id,tag,shape:'box',minX:x-w/2,maxX:x+w/2,minZ:z-d/2,maxZ:z+d/2,minY:lo,maxY:hi});
  for(const c of originals){
    const {x,z}=colliderBounds(c);
    if(c.tag==='sofa'){
      result.colliders.push(box(`${c.id}-base`,c.tag,x,z,10.5,3.7,0,1.6),box(`${c.id}-seat`,c.tag,x,z-.24,9.5,2.65,1.37,2.07),box(`${c.id}-back`,c.tag,x,z+1.32,9.52,1.01,1.44,4.06));
      for(const side of [-1,1])result.colliders.push(box(`${c.id}-arm-${side}`,c.tag,x+side*5.05,z,.7,3.5,.5,2.2));
    }else{
      // These two chairs are placed at ±90 degrees in createLiving().
      const direction=x<23?-1:1;
      result.colliders.push(box(`${c.id}-base`,c.tag,x,z,4.1,4.4,.015,1.265),box(`${c.id}-seat`,c.tag,x-direction*.25,z,2.8,3.35,1.155,1.805),box(`${c.id}-back`,c.tag,x+direction*1.4,z,1.45,4.15,1.14,4.96));
      for(const side of [-1,1])result.colliders.push(box(`${c.id}-arm-${side}`,c.tag,x+direction*.05,z+side*1.85,3.65,.72,.75,2.55));
    }
  }
  return validateMapDocument(result);
}
